/**
 * Central theme module (LOCKED-DESIGN 18.8): the single code-owned source for
 * palette, trash/avatar/arena material parameters, the lighting rig, and
 * post-processing parameters. Values are a 1:1 extraction of the literals that
 * previously lived inline in the renderer/arena/gameplay files — this module
 * changes WHERE the look lives, not what it looks like. Arena presets remain
 * the authoring contract; this maps preset names to appearance.
 *
 * HUD/shell CSS stays in its own stylesheet until the Phase 3 token pass; the
 * palette is exported here so that pass can consume the same source.
 */

import type { QualityLevel } from "@host/types.js";

/** #rrggbb string for canvas/CSS use. */
export function cssHex(color: number): string {
  return `#${color.toString(16).padStart(6, "0")}`;
}

/** rgba(...) string for canvas/CSS use. */
export function cssRgba(color: number, alpha: number): string {
  return `rgba(${(color >> 16) & 0xff},${(color >> 8) & 0xff},${color & 0xff},${alpha})`;
}

/**
 * Signal-color taxonomy (LOCKED-DESIGN 2.2/18.1): blue = arena anchors,
 * orange = furnace receivers, green/amber/red = safe/technical/hazard tiers,
 * teal = conveyance.
 */
export const PALETTE = {
  anchorBlue: 0x35b8ff,
  receiverOrange: 0xff9435,
  tierSafe: 0x57e3a1,
  tierTechnical: 0xffbd55,
  tierHazard: 0xff596f,
  conveyance: 0x3bc9a8,
  avatar: 0xdfe7f2,
  background: 0x05060a,
  fog: 0x07090f,
} as const;

/** Receiver tier accents keyed the way the arena/simulation name them. */
export const RECEIVER_TIER_COLORS: Record<"safe" | "technical" | "hazard", number> = {
  safe: PALETTE.tierSafe,
  technical: PALETTE.tierTechnical,
  hazard: PALETTE.tierHazard,
};

/**
 * Chain tier accents, ×1 through ×3 (LOCKED-DESIGN 8.2 five visible tiers).
 * Reward bursts, payoff popups, and the chain meter tint from this ladder so
 * "how hot is my Chain" reads as one escalating color story everywhere.
 */
export const CHAIN_TIER_COLORS = [
  PALETTE.conveyance,
  PALETTE.tierSafe,
  PALETTE.tierTechnical,
  PALETTE.receiverOrange,
  PALETTE.tierHazard,
] as const;

/** Feedback/effect accents (LOCKED-DESIGN 18.4). All new effect colors live here. */
export const FEEDBACK_COLORS = {
  /** placement denial / interlock refusal */
  deny: 0xff3b4d,
  /** zero-reward outcomes: MISS beat, LATE piece tags */
  muted: 0x8b95a7,
  /** STALE route micro-label (Section 8.3) */
  stale: PALETTE.tierTechnical,
  /** partial-load subtotal popup — cooler than the clean payoff */
  partial: 0x9fc4e8,
  /** blast core / shockwave */
  blastCore: 0xff6a1f,
  /** scattered husk-dark debris */
  debris: 0x5b4038,
  /** critical-overflow alarm treatment */
  alarm: PALETTE.tierHazard,
  /** pre-critical overflow pressure (24+) ambient warning */
  overflowWarning: 0xffb84d,
  /** telegraphs: chute mini-batch flash, shutter warning edge lights */
  telegraph: 0xffb84d,
  /** furnace-death white-out + respawn ring */
  death: 0xf4f7fb,
  /** reinforced-glass bulkhead stress: the sealed-chamber hit VFX (18.6 glass beat) */
  bulkhead: 0x9fdcff,
  /** dispatch completion gold: ticket toast + the +8 Grade attribution chip (Section 9) */
  dispatch: 0xffcf7a,
} as const;

/**
 * Overflow location buckets (LOCKED-DESIGN Section 7 / 18.10 third dogfood):
 * the meter's "where is the bulk" breakdown reuses the locked signal taxonomy —
 * blue = portal transit (anchors/transfer), orange = rejected at the receiver
 * chamber (reject tray), teal = the return belt (conveyance) — so the segments
 * speak the same color language as the world. Floor/beam use neutral non-signal
 * tones; position + label carry their identity (never hue alone).
 */
export const OVERFLOW_BUCKET_COLORS = {
  /** loose in the arena */
  floor: FEEDBACK_COLORS.muted,
  /** held in the gravity beam */
  beam: PALETTE.avatar,
  /** in portal transit / furnace processing */
  transit: PALETTE.anchorBlue,
  /** rejected in the chamber (reject tray) */
  chamber: PALETTE.receiverOrange,
  /** returning on the belt */
  belt: PALETTE.conveyance,
} as const;

/**
 * Reject-return assembly structural materials (LOCKED-DESIGN Section 6 /
 * 18.10 third dogfood): the chamber collection-belt tray, the east duct
 * hood/mouth, and the return-hatch collar beside the sorting island. Neutral
 * painted steel + a deep teal-cast collar body under the conveyance emissive —
 * the teal GLOW carries the signal; these tokens only paint structure.
 */
export const RETURN_ASSEMBLY = {
  steel: { color: 0x59616b, roughness: 0.46, metalness: 0.62 },
  dark: { color: 0x14181c, roughness: 0.92, metalness: 0.1 },
  collar: { color: 0x1c4048, emissive: PALETTE.conveyance, emissiveIntensity: 0.5, roughness: 0.5, metalness: 0.4 },
} as const;

/**
 * Sanitation Grade band lighting drift (LOCKED-DESIGN 18.4 "arena-wide
 * lighting moment", 2.2 "Grade subtly changes lighting, soot, cleanliness and
 * ambience without reducing object visibility"). Every band stays LIGHT — the
 * drift walks warmth/cleanliness from a faintly sooty-cool C up to a warm,
 * spotless S, never darkening. `key`/`rim` are the directional hues that lerp;
 * `ground` is the hemisphere bounce (concrete warmth) and `haze` gently retints
 * the enclosed-room background/fog. C is exactly the base rig.
 */
export const GRADE_BAND_LIGHTING: Record<"C" | "B" | "A" | "S", { key: number; rim: number; ground: number; haze: number }> = {
  C: { key: 0xe9edf3, rim: 0x8ea6c4, ground: 0x8f877b, haze: 0xa9b4bd },
  B: { key: 0xeef2f7, rim: 0x9fb4d0, ground: 0x998f80, haze: 0xb0bbc4 },
  A: { key: 0xf4f7fc, rim: 0xb0c4dc, ground: 0xa79a86, haze: 0xb9c3cb },
  S: { key: 0xfff4de, rim: 0xe6cc9c, ground: 0xc0a982, haze: 0xc8cabf },
};

/**
 * Sanitation Grade band letters → accent hex (LOCKED-DESIGN 8.3 Grade bands,
 * 18.8 single palette owner). The in-run HUD grade meter (Hud.ts) AND the
 * results-shell grade badge (shell/tokens.ts) both source from this one map, so
 * a given band letter renders the same color in gameplay and on the end screen.
 * Bands walk the signal-color tiers: C = muted, B = safe green, A = technical
 * amber, S = the S-band rim gold that also tints the arena lighting.
 */
export const GRADE_BAND_HEX: Record<"C" | "B" | "A" | "S", string> = {
  C: cssHex(FEEDBACK_COLORS.muted),
  B: cssHex(RECEIVER_TIER_COLORS.safe),
  A: cssHex(RECEIVER_TIER_COLORS.technical),
  S: cssHex(GRADE_BAND_LIGHTING.S.rim),
};

interface TrashMaterialTokens {
  color: number;
  emissive?: number;
  emissiveIntensity?: number;
  roughness: number;
  metalness: number;
}

export interface TrashKindTheme {
  /** signature accent — release trails and any per-kind UI accents */
  accent: number;
  /** BODY material for the instanced pieces (group 0 of the merged geometry) */
  material: TrashMaterialTokens;
  /**
   * TRIM material (group 1) — the structural second tone the 18.27 identity
   * pass added: crate posts/rails, can rims/ribs, green gasket/seams, red's
   * dark fuse collar, husk ember knots. Emissive discipline: only the husk's
   * ember knots may glow here, faintly — red's danger emissive stays on the
   * BODY exactly as before, so the identity pass adds zero new bright mass to
   * the portal-outshine budget.
   */
  trim: TrashMaterialTokens;
}

/** One source for the trash palette (was duplicated across Engine and Trash). */
export const TRASH_THEME: Record<"crate" | "can" | "green" | "red" | "husk", TrashKindTheme> = {
  crate: {
    accent: 0xff8c42,
    material: { color: 0xff8c42, emissive: 0xff8c42, emissiveIntensity: 0.12, roughness: 0.7, metalness: 0.1 },
    trim: { color: 0x8f4a1e, roughness: 0.8, metalness: 0.15 },
  },
  can: {
    accent: 0x9fc4e8,
    material: { color: 0x9fc4e8, roughness: 0.3, metalness: 0.85 },
    trim: { color: 0xdde9f2, roughness: 0.22, metalness: 0.9 },
  },
  green: {
    accent: 0x3bffc0,
    material: { color: 0x3bffc0, emissive: 0x3bffc0, emissiveIntensity: 0.25, roughness: 0.35, metalness: 0.2 },
    trim: { color: 0x0f6b52, roughness: 0.6, metalness: 0.1 },
  },
  red: {
    accent: 0xff334f,
    material: { color: 0xff334f, emissive: 0xff1738, emissiveIntensity: 1.2, roughness: 0.3, metalness: 0.15 },
    trim: { color: 0x2a2126, roughness: 0.5, metalness: 0.4 },
  },
  husk: {
    accent: 0x3a2426,
    material: { color: 0x211a1c, emissive: 0x5b1d18, emissiveIntensity: 0.15, roughness: 0.95, metalness: 0.05 },
    trim: { color: 0x2b1a16, emissive: 0xff5a2a, emissiveIntensity: 0.35, roughness: 0.9, metalness: 0 },
  },
};

/**
 * Hero-piece materials (LOCKED-DESIGN 18.3): the sanitation-unit player robot,
 * the three receiver machinery tiers, the furnace maw, and the reject-release
 * handle. All procedural kit-bash factories in HeroAssets.ts source exclusively
 * from this block, so a palette pass touches one file. Tier RISK stays encoded
 * in FORM (aperture size, guard structure, chevrons) — these tokens only paint.
 */
export const HERO_THEME = {
  /** sanitation-unit robot: hull stays the LIGHTEST moving object in the room */
  robot: {
    hull: { color: PALETTE.avatar, roughness: 0.38, metalness: 0.32 },
    livery: { color: 0xc3cfdd, roughness: 0.52, metalness: 0.28 },
    joint: { color: 0x4d555f, roughness: 0.45, metalness: 0.6 },
    tread: { color: 0x30363d, roughness: 0.82, metalness: 0.28 },
    /** eye/visor: the same conveyance-teal identity the capsule nose carried */
    visor: { color: 0x141c22, emissive: PALETTE.conveyance, emissiveIntensity: 2.1, roughness: 0.28, metalness: 0.2 },
    emitter: { color: 0x39424c, roughness: 0.35, metalness: 0.6 },
    emitterCore: { color: 0x9fe8dc, emissive: PALETTE.conveyance, roughness: 0.3, metalness: 0.1 },
    /** beam-emitter core emissive by beam state (renderer eases between) */
    emitterIdle: 0.45,
    emitterHold: 2.6,
    /** seeded facility-livery decal (canvas): unit number + hazard stripe */
    decal: { plate: "#dfe7f2", ink: "#2b333d", stripe: "#f0b83a", stripeDark: "#1c1f24", wear: "rgba(43,51,61,0.16)", unit: "SU-07" },
  },
  /** receiver machinery: painted-steel guards; tier accents keep the locked hues */
  receiver: {
    guard: { color: 0x6a727c, roughness: 0.5, metalness: 0.58 },
    guardDark: { color: 0x454d57, roughness: 0.55, metalness: 0.6 },
    funnel: { color: 0x8a939c, roughness: 0.46, metalness: 0.52 },
    /** plain fallback for the hazard chevron plates when canvas is unavailable */
    chevronFallback: { color: 0x1a1410, roughness: 0.7, metalness: 0.2 },
  },
  /** furnace maw: segmented teeth, glowing throat treatment, intake rollers */
  furnace: {
    tooth: { color: 0x2c2327, emissive: 0x7a2214, emissiveIntensity: 0.35, roughness: 0.52, metalness: 0.58 },
    frame: { color: 0x3b3238, roughness: 0.5, metalness: 0.55 },
    roller: { color: 0x35252a, emissive: 0x7a2214, emissiveIntensity: 0.3, roughness: 0.48, metalness: 0.62 },
    emberStrip: { color: 0xffb26a, emissive: 0xff4f16, emissiveIntensity: 1.1, roughness: 0.5, metalness: 0.2 },
    /** recessed dark throat behind the fire plane */
    throatBack: { color: 0x050405, emissive: 0x180806, emissiveIntensity: 0.22, roughness: 0.92, metalness: 0 },
    /** the glowing fire plane (GrayBoxArena drives its flicker) */
    fire: { color: 0xffb26a, emissive: 0xff4f16, emissiveIntensity: 1.4, roughness: 0.5, metalness: 0 },
    /** banded heat-gradient canvas stops for the fire plane */
    gradient: { base: "#080305", deep: "#2a0707", mid: "#b92e12", hot: "#ff7a2a", top: "#ffd08a", bar: "rgba(12,4,5,.72)", sill: "rgba(255,235,190,.52)" },
    /** additive heat-shimmer sheet colors (canvas) — dropped on the low tier */
    shimmer: { hot: "rgba(255,170,90,0.34)", soft: "rgba(255,110,40,0.18)", opacity: 0.42 },
  },
  /** reject-release lever: teal = conveyance/recovery (Section 6 fiction) */
  handle: {
    base: { color: 0x59616b, roughness: 0.46, metalness: 0.62 },
    arm: { color: 0x8a939c, roughness: 0.42, metalness: 0.58 },
    grip: { color: 0x1c4048, roughness: 0.62, metalness: 0.3 },
    signal: { color: 0x235058, emissive: PALETTE.conveyance, emissiveIntensity: 0.6, roughness: 0.4, metalness: 0.35 },
  },
} as const;

/**
 * Background, fog, and image-based-lighting strength for the scene
 * (LOCKED-DESIGN 18.1 light-dominant clean-industrial rebase). The "neon void"
 * near-black is retired: the background is a bright room haze and the fog fades
 * far enclosure walls INTO that haze, so no black void band survives at either
 * locked resolution. `background`/`fog` are scene-only tokens — the dark shell
 * chrome keeps PALETTE.background. `fogSky` is the hemisphere-lit ceiling tone.
 */
export const SCENE_ENVIRONMENT = {
  background: 0xa7b2bb,
  // LOCKED-DESIGN 18.13 (56 x 40 arena): fog near 38->40, far 170->190 as the
  // worst in-frame distance grows ~72->~84 m — a mild intentional depth cue;
  // SceneRenderer far-clip 200 still > fog far.
  fog: { color: 0xafb9c1, near: 40, far: 190 },
  environmentIntensity: 0.5,
} as const;

/**
 * Lighting rig (LOCKED-DESIGN 18.1/2.2): a structured clean daylight-grade
 * facility rig. The cool key + fill + rim read as an evenly lit painted-steel
 * room; the warm furnace practical (built in the arena) stays the brightest,
 * warmest focal anchor — the warm-vs-cool contrast is the lighting thesis.
 * `key` is the single shadow caster (perf, 13.1); `fill`/`rim` never cast.
 * `key`/`rim` hues also drift with the Grade band (GRADE_BAND_LIGHTING), so
 * their base colors match band C.
 */
export const LIGHTING_RIG = {
  hemisphere: { sky: 0xe4ecf6, ground: GRADE_BAND_LIGHTING.C.ground, intensity: 0.58 },
  key: {
    color: GRADE_BAND_LIGHTING.C.key,
    intensity: 1.62,
    position: [15, 24, 9],
    shadowMapSize: 2048,
    // LOCKED-DESIGN 18.13 (56 x 40 arena): extent 42->46 covers the deepened
    // half-diagonal (sqrt(28^2+20^2)=34.4 + ~11.6 margin = 46). Softer edges
    // are accepted; shadowFar 72 unchanged (light (15,24,9) -> farthest floor
    // corner (-28,0,-20) = 57.2 < 72 still bounds the scene depth).
    shadowExtent: 46,
    shadowFar: 72,
    shadowBias: -0.0004,
  },
  /** cool front-left fill that lifts the camera-facing faces (no shadow, no crush) */
  fill: { color: 0xb9c6d8, intensity: 0.66, position: [-13, 11, 15] },
  rim: { color: GRADE_BAND_LIGHTING.C.rim, intensity: 0.62, position: [-10, 9, -14] },
} as const;

/** Structural accents the arena builder uses outside the preset table. */
export const ARENA_ACCENTS = {
  anchorSteel: 0x9aa6ae,
  /**
   * Portal-compatible anchor-panel body (LOCKED-DESIGN 18.1). The light-dominant
   * rebase value-lifted every wall/platform/chamber surface but left the anchor
   * slab at a neon-era near-black; this is the painted-steel body that lands it
   * back in the room's mid-light band, with a restrained blue portal cast so it
   * still reads as an aimable panel (not a flat wall) and the anchorSteel frame
   * still separates from it. Receiver apertures stay dark by design (recessed
   * targets), so only the A-anchor body is rebased here.
   */
  anchorPanel: { color: 0x86929e, emissive: 0x27546b, emissiveIntensity: 0.08, roughness: 0.62, metalness: 0.44 },
} as const;

/**
 * Visual-only sealed-room enclosure (LOCKED-DESIGN 18.1 "enclosed ceiling and
 * walls"; 18.8 collider extraction — geometry-only, no colliders). Tokens for
 * the ceiling, tall far/side walls, roof trusses, and light-fixture practicals
 * the GrayBoxArena builder raises around the play area so the facility reads as
 * a room, never a floating island. Dimensions are in world units; the follow
 * camera (y≈13.5, pitched down) never looks above horizontal, so the ceiling
 * caps the box for wide/editor framings while the tall walls fill the frame.
 */
export const ENCLOSURE = {
  /** painted-steel structural shell */
  wall: { color: 0xa3abb2, roughness: 0.62, metalness: 0.28 },
  /** upper wall band above the safety-graphic zone — slightly lighter */
  wallUpper: { color: 0xb7bec4, roughness: 0.66, metalness: 0.22 },
  /** ceiling deck (only seen in wide/editor shots) */
  ceiling: { color: 0xbabfc4, roughness: 0.82, metalness: 0.1 },
  /** dark structural trusses/beams under the deck */
  truss: { color: 0x5a626b, roughness: 0.55, metalness: 0.55 },
  /** ceiling light-fixture housings */
  fixture: { color: 0x2b2f36, roughness: 0.5, metalness: 0.6 },
  /** fixture lamp face (emissive, cool — a practical, not bloom mood) */
  lamp: { color: 0xf4f8ff, emissive: 0xdfe8f5, emissiveIntensity: 1.15 },
  /** painted safety-graphic block on the far wall (deadpan corporate signage) */
  signage: { color: 0xf0b83a, roughness: 0.7, metalness: 0.12 },
  /** hazard chevron paint flanking the furnace maw */
  chevron: { color: 0x1c1f24, roughness: 0.8, metalness: 0.1 },
} as const;

/**
 * Reinforced-glass bulkhead presence (LOCKED-DESIGN 2.1 broad glass bulkhead,
 * 18.1 "reinforced glass with real physical presence"). This is the story-
 * critical sealed barrier — direct throws must visibly FAIL against it. Presence
 * is built from a structural steel mullion/transom grid + heavy perimeter frame,
 * a laminated edge tint, a STATIC reflection treatment (the room envMap at a
 * raised intensity plus a baked sheen streak — NO per-frame planar reflection on
 * ANY tier, so the low tier pays nothing extra), and seeded smudge/impact
 * graphics so it reads as a heavy, used barrier while the Processing Chamber
 * stays visible through it. The enforced glass-beat impact VFX (18.6) still lands
 * on this surface (the smudge/impact overlay is wired into GrayBoxArena.glassFx).
 */
export const GLASS = {
  /** the pane: cool laminated tint, low roughness so the room envMap reflects */
  pane: { color: 0xbfe3f2, opacity: .14, roughness: .06, metalness: 0, envMapIntensity: 1.65, envMapIntensityLow: 1.05 },
  /** structural steel mullion/transom grid + heavy perimeter frame */
  frame: { color: 0x757e87, roughness: .48, metalness: .64 },
  /** laminated-glass edge tint band (cool green cast) baked into the overlay */
  edge: "rgba(20,54,63,0.55)",
  /** seeded smudge / dust / impact overlay (normal-blended, in glassFx) */
  overlay: {
    opacity: .55,
    smudge: "rgba(28,40,46,0.17)",
    dust: "rgba(22,30,36,0.11)",
    streak: "rgba(210,232,240,0.10)",
    impact: "rgba(223,244,255,0.8)",
    impactCore: "rgba(255,255,255,0.92)",
  },
  /** baked reflection sheen streak (additive, high/medium tier only) */
  sheen: { opacity: .5, band: "rgba(223,244,255,0.5)" },
} as const;

/**
 * Diegetic safety-graphic signage (LOCKED-DESIGN 18.1/18.2): route, multiplier,
 * and infeed identifiers render as deadpan corporate STENCIL signs — an
 * illuminated lightbox plate, a role/tier FORM glyph (reticle / aperture / guard
 * / chevron / funnel), and stencilled text — instead of floating monospace
 * pills. FORM + text carry the identity so the tier triad stays colorblind-safe
 * (not hue-only). The sign face survives as the sanctioned accessibility + QA
 * overlay (18.1); painted on-surface markings reinforce it on the facility.
 */
export const SIGNAGE = {
  /** illuminated lightbox sign face (canvas) */
  plate: {
    body: "rgba(11,15,21,0.9)",
    bezel: "rgba(255,255,255,0.14)",
    text: "#f4f7fb",
    rivet: "rgba(233,240,247,0.5)",
  },
  /** worn off-white stencil paint for painted on-surface floor markings */
  paint: { stencil: 0xdfe7f2 },
} as const;

/** Camera transition timings (LOCKED-DESIGN 2.2 chamber blend — short and readable). */
export const CAMERA_BLEND = {
  /** arena ⇄ chamber view blend duration in seconds */
  chamberSeconds: 0.45,
} as const;

/**
 * Scalable-visuals quality tiers (LOCKED-DESIGN 13.1 "stable 60 fps ... with
 * scalable visuals"; 18.5 settings surface). 'high' IS the current pipeline —
 * lower tiers only remove cost, never change the scene or the simulation.
 * 'potato' is a legacy host alias and maps to the low profile.
 */
export interface QualityTierParams {
  /** devicePixelRatio is clamped to this before it reaches the renderer */
  dprCap: number;
  /** multiplier on the authored bloom intensity; 0 removes the bloom pass */
  bloomScale: number;
  /** SMAA pass on/off (renderer antialias stays off — SMAA is the only AA) */
  smaa: boolean;
  /** key-light shadow casting + the shadow-map pass */
  shadows: boolean;
  /** key-light shadow map resolution (square) */
  shadowMapSize: number;
  /** PCFSoft filtering when true, basic PCF when false */
  softShadows: boolean;
}

const LOW_QUALITY_TIER: QualityTierParams = { dprCap: 1, bloomScale: 0, smaa: false, shadows: false, shadowMapSize: 1024, softShadows: false };

export const QUALITY_TIERS: Record<QualityLevel, QualityTierParams> = {
  high: { dprCap: 2, bloomScale: 1, smaa: true, shadows: true, shadowMapSize: LIGHTING_RIG.key.shadowMapSize, softShadows: true },
  medium: { dprCap: 1.5, bloomScale: 0.5, smaa: true, shadows: true, shadowMapSize: 1024, softShadows: true },
  low: LOW_QUALITY_TIER,
  potato: LOW_QUALITY_TIER,
};

export interface ArenaBoxPresetTheme {
  color: number;
  opacity?: number;
  emissive?: number;
  emissiveIntensity?: number;
  basic?: boolean;
  depthWrite?: boolean;
  /** MeshStandardMaterial roughness — per-preset material identity (18.1) */
  roughness?: number;
  /** MeshStandardMaterial metalness — concrete≈0, painted steel≈.3, machinery≈.6 */
  metalness?: number;
}

/**
 * Untextured box-preset appearance (preset names are the authoring contract).
 * Light-dominant clean-industrial rebase (LOCKED-DESIGN 18.1): painted steel,
 * industrial machinery, and structural surfaces read by value + roughness +
 * metalness, not four near-identical dark blue-grays. Signal presets (guides,
 * baffle, shutter, recovery) keep the gameplay-locked taxonomy.
 */
export const ARENA_BOX_PRESETS: Record<string, ArenaBoxPresetTheme> = {
  // painted steel (structure) → mid-light, low metalness
  wall: { color: 0xaab2b9, roughness: .58, metalness: .3 },
  platform: { color: 0x9fa7ad, roughness: .5, metalness: .38 },
  chamber: { color: 0x8f979f, roughness: .62, metalness: .26 },
  "glass-frame": { color: 0x8c959d, roughness: .42, metalness: .55 },
  // industrial machinery → darker metal
  machine: { color: 0x59616b, roughness: .46, metalness: .62 },
  furnace: { color: 0x3b3238, roughness: .5, metalness: .55 },
  // reinforced glass with real presence (18.1) — cooler, a touch more opaque
  glass: { color: 0xbfe3f2, opacity: .13, depthWrite: false, roughness: .08, metalness: 0 },
  // caution machinery (purge) — painted safety red/amber
  purge: { color: 0x8a463a, roughness: .55, metalness: .35, emissive: 0x3a1410, emissiveIntensity: .12 },
  // receiver approach lanes — painted-steel platforms with a restrained tier tint
  "safe-lane": { color: 0x5c7d6a, roughness: .55, metalness: .3 }, "technical-lane": { color: 0x7d7350, roughness: .55, metalness: .3 }, "hazard-lane": { color: 0x7d4c55, roughness: .55, metalness: .3 },
  // painted floor guide markings (signage) — keep the anchor-blue accent
  "blue-guide": { color: 0x2a4f6b, emissive: PALETTE.anchorBlue, emissiveIntensity: .5 },
  "safe-guide": { color: PALETTE.tierSafe, opacity: .72, basic: true }, "technical-guide": { color: PALETTE.tierTechnical, opacity: .72, basic: true }, "hazard-guide": { color: PALETTE.tierHazard, opacity: .18, basic: true, depthWrite: false },
  "hazard-baffle": { color: 0xff596f, emissive: 0xff334f, emissiveIntensity: .5 }, shutter: { color: 0xffb84d, opacity: .72, emissive: 0xff7a2a, emissiveIntensity: .65 },
  // chamber recovery affordances (Section 3.4/6): teal = conveyance/recovery
  "return-pad": { color: 0x1c4048, emissive: PALETTE.conveyance, emissiveIntensity: .5 },
  "recovery-handle": { color: 0x235058, emissive: PALETTE.conveyance, emissiveIntensity: .6 },
};

export const ARENA_BOX_FALLBACK: ArenaBoxPresetTheme = { color: 0xa7aeb5, roughness: .6, metalness: .3 };

/**
 * Material parameters for the procedurally textured presets. The floor is now
 * bright matte concrete (its aggregate/wear/grime live in the seeded canvas
 * texture); the neon emissive seam glow is retired (18.1) — only a faint cool
 * lift remains so painted lane markings read without blooming. The conveyor is
 * a matte rubber belt, not an emissive plane.
 */
export const ARENA_TEXTURED_PRESETS = {
  floor: { color: 0xffffff, emissive: 0x2b3540, emissiveIntensity: .12, roughness: .96, metalness: .04 },
  "hazard-stripe": { emissive: PALETTE.receiverOrange, emissiveIntensity: .3, roughness: .7 },
  conveyor: { color: 0xffffff, emissive: PALETTE.conveyance, emissiveIntensity: .14, roughness: .82, metalness: .12 },
} as const;

/** Canvas color inputs for the procedural textures in textures.ts. */
export const TEXTURE_THEME = {
  floor: {
    /** bright-but-mid concrete slab base (a legible ground, not near-white) */
    base: "#9ea3a7",
    /** per-panel value: base + prng * range (mid concrete band with real variation) */
    panelValue: { base: 150, range: 30 },
    /** aggregate speckle: darker aggregate + lighter flecks */
    aggregateDark: "rgba(40,44,50,0.10)",
    aggregateLight: "rgba(255,255,255,0.14)",
    /** darker expansion-joint / panel seams (diffuse, not glowing) */
    seam: "rgba(58,64,72,0.55)",
    /** localized grime blotches (2.2 "localized grime without visual clutter") */
    grime: "rgba(46,50,54,0.16)",
    /** faint painted circulation accent — barely-there, retires the neon seam */
    accentSeam: cssRgba(PALETTE.conveyance, 0.28),
  },
  /** painted-steel panels: seams, bolt rows, scuffs, localized grime */
  steel: {
    base: "#aab2b9",
    panelValue: { base: 168, range: 16 },
    seam: "rgba(52,58,66,0.5)",
    bolt: "rgba(38,42,48,0.55)",
    boltHi: "rgba(255,255,255,0.16)",
    scuff: "rgba(40,44,50,0.10)",
    grime: "rgba(44,48,54,0.14)",
  },
  hazard: {
    base: "#1a1410",
    stripe: cssHex(PALETTE.receiverOrange),
  },
} as const;

/**
 * Post pipeline parameters (bloom → vignette → ACES tone map → SMAA). Rebased
 * light-dominant (LOCKED-DESIGN 18.1): bloom is accent-only — a high luminance
 * threshold lets bright concrete stay crisp while signal glows/furnace still
 * flare — and the vignette is a gentle edge settle, not the near-black frame
 * that reinforced the old void.
 */
export const POST_FX_PARAMS = {
  bloom: { intensity: 0.72, luminanceThreshold: 0.78, luminanceSmoothing: 0.22, mipmapBlur: true },
  vignette: { darkness: 0.28, offset: 0.42 },
} as const;
